import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { SharedService } from '@app/core';
import { environment } from '@env/environment';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

/**
 * Standalone page component for Hyperpure cart iframe with user authentication.
 * Requires both user authentication token AND Hyperpure credentials.
 * 
 * This component validates the user's authentication token before loading the iframe.
 * It initializes the iframe and loads the cart view using the message topic:
 * 1. hyperpure_frame_load_cart - Load cart view
 *
 * URL Format with User Token:
 * /purchase-manager/hyperpure-cart?userToken=xxx&token=xxx&store_uuid=xxx&email=xxx&phone=xxx
 *
 * Or use session storage with key 'hyperpureLoginData' (user must still be authenticated)
 */
@Component({
    selector: 'app-hyperpure-cart-iframe',
    templateUrl: './hyperpure-cart-iframe.component.html',
    styleUrls: ['./hyperpure-cart-iframe.component.scss']
})
export class HyperpureCartIframeComponent implements OnInit, OnDestroy {

    @ViewChild('iframeElement') iframeElement!: ElementRef;

    // Iframe state
    iframeUrl: string = 'https://dev.hyperpure.com/in/Init';
    iframeUrlSafe: SafeResourceUrl | null = null;
    showIframe = false;
    isLoading = true;

    // Login data
    hyperpureLoginData: any = null;

    // User authentication
    private userToken: string | null = null;
    private isUserAuthenticated = false;
    private userInfo: any = null;

    // Security
    private trustedOrigin = environment.hyperpureTrustedOrigin;
    private listenerCalled = false;
    private frameLoadedHandled = false;

    // Public reference for template
    public environment = environment;

    // Lifecycle
    private destroy$ = new Subject<void>();

    constructor(
        private sanitizer: DomSanitizer,
        private route: ActivatedRoute,
        private router: Router,
        private http: HttpClient,
        private sharedService: SharedService,
        private priceDiscoveryService: PriceDiscoveryService
    ) { }

    ngOnInit(): void {
        this.setupMessageListener();
        this.authenticateAndLoad();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        document.body.style.overflow = '';
    }

    /**
     * Step 1: Authenticate user, then load Hyperpure data
     */
    private authenticateAndLoad(): void {
        this.route.queryParams.pipe(take(1), takeUntil(this.destroy$)).subscribe(params => {
            console.log('🔍 Query params received');

            // Extract user token - properly decode URL-encoded token
            let rawToken = params['userToken'] || params['user_token'];

            if (!rawToken) {
                this.showError('User authentication token is missing. Access denied.');
                return;
            }

            // URL decode the token to handle special characters (+, /, =)
            // Angular's ActivatedRoute might not fully decode some characters
            try {
                this.userToken = decodeURIComponent(rawToken);
                console.log('🔐 Token decoded, length:', this.userToken.length);
            } catch (e) {
                console.error('Failed to decode token:', e);
                this.userToken = rawToken;
            }

            // Validate user token
            console.log('🔐 Validating user token...');
            this.validateUserToken(this.userToken).subscribe(
                (isValid) => {
                    if (isValid) {
                        console.log('✅ User authenticated successfully');
                        this.isUserAuthenticated = true;

                        // Now load Hyperpure login data (params already captured)
                        this.loadLoginDataFromParams(params);
                    } else {
                        this.showError('Invalid or expired user token. Please login again.');
                    }
                },
                (error) => {
                    console.error('❌ User authentication failed:', error);
                    this.showError('Authentication failed. Please try again.');
                }
            );
        });
    }

    /**
     * Validate user token against backend
     */
    private validateUserToken(token: string): Observable<boolean> {
        const apiUrl = `${environment.authentication.url}${environment.authentication.path}get-user-profile`;
        console.log('📡 Calling API:', apiUrl);
        console.log('🔑 Token (first 50 chars):', token.substring(0, 50) + '...');
        console.log('🔑 Token length:', token.length);

        // Call your authentication service to verify the token
        return this.http.get<any>(apiUrl, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).pipe(
            map((response: any) => {
                console.log('📥 API Response:', response);
                // Check if response is successful (MongoDB uses _id field)
                if (response && (response._id || response.id)) {
                    console.log('✅ User validated:', response.name || `${response.firstName} ${response.lastName}`);
                    console.log('✅ Email:', response.email);
                    console.log('✅ Company:', response.company?.name);
                    // Store user info for later use
                    this.userInfo = response;
                    return true;
                }
                console.error('❌ Invalid response structure:', response);
                return false;
            }),
            catchError((error) => {
                console.error('❌ Token validation failed - Full error:', error);
                console.error('Error status:', error.status);
                console.error('Error statusText:', error.statusText);
                console.error('Error error object:', error.error);

                // Try to parse the error message
                let errorMessage = 'Authentication failed';
                if (error.error) {
                    if (typeof error.error === 'string') {
                        errorMessage = error.error;
                    } else if (error.error.message) {
                        errorMessage = error.error.message;
                    } else if (error.error.error) {
                        errorMessage = error.error.error;
                    }
                }

                console.error('Parsed error message:', errorMessage);
                return of(false);
            })
        );
    }

    /**
     * Step 2: Load login data from already-captured query parameters
     * (Only called after user is authenticated)
     * Uses pre-captured params to avoid duplicate queryParams subscription.
     */
    private loadLoginDataFromParams(params: any): void {
        console.log('🔍 Loading Hyperpure credentials');

        if (params['store_uuid']) {
            const storeUuid = params['store_uuid'];
            // Resolve entityId: prefer query param, then derive from userInfo (get-user-profile response)
            let entityId = params['entity_id'] || params['entityId'] || '';
            if (!entityId && this.userInfo) {
                // The get-user-profile response contains company.outlets[] where each outlet has _id/id
                const company = this.userInfo.company;
                if (company?.outlets?.length) {
                    console.log('🆔 [HYPERPURE] Searching for outlet matching storeUuid:', storeUuid);
                    console.log('🆔 [HYPERPURE] Available outlet UUIDs:', company.outlets.map((o: any) => o.hyperpureStoreUuid || o.hyperpure_store_uuid).filter(Boolean).join(', '));
                    
                    // Find outlet matching the store_uuid
                    const matchedOutlet = company.outlets.find((o: any) => 
                        o.hyperpureStoreUuid === storeUuid || 
                        o.hyperpure_store_uuid === storeUuid
                    );
                    if (matchedOutlet) {
                        console.log('🆔 [HYPERPURE] Match found! Outlet ID:', matchedOutlet._id || matchedOutlet.id);
                        entityId = matchedOutlet._id || matchedOutlet.id || '';
                    } else {
                        console.warn('⚠️ [HYPERPURE] No matching outlet found for storeUuid:', storeUuid);
                    }
                }
                if (!entityId) {
                    entityId = company?._id || company?.id || '';
                    if (entityId) console.log('🆔 [HYPERPURE] Falling back to company ID:', entityId);
                }
                if (!entityId) {
                    entityId = this.userInfo._id || this.userInfo.id || '';
                    if (entityId) console.log('🆔 [HYPERPURE] Falling back to user ID:', entityId);
                }
            }
            console.log('🆔 [HYPERPURE] Resolved entityId:', entityId);
            const email = params['email'] || '';
            const phone = params['phone'] || '';

            console.log('🔄 [HYPERPURE] Calling sign-in API for fresh token + clientSessionId...');

            // Call sign-in API to get fresh token + clientSessionId (60s TTL)
            const requestPayload = {
                store_uuid: storeUuid,
                store_details: [],
                user_details: {}
            };

            this.priceDiscoveryService.getHyperpureLoginData(entityId, requestPayload, this.userToken)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (data: any) => {
                        console.log('✅ [HYPERPURE] Fresh sign-in successful:', data);

                        // Build fresh login data with new token + clientSessionId
                        const storeDetailsArray = [{
                            store_id: data.hyperpureIdMd5 || params['store_id'] || '',
                            restaurant_name: data.storeName || params['storeName'] || params['store_name'] || '',
                            restaurant_gstno: data.storeGst || params['storeGst'] || params['store_gst'] || '',
                            store_address: data.storeAddress || params['storeAddress'] || params['store_address'] || '',
                            store_pincode: data.storePincode || params['storePincode'] || params['store_pincode'] || ''
                        }];
                        const all_store_details = JSON.stringify(storeDetailsArray);

                        const allStoreObj = {
                            store_id: data.store_id || data.storeId || params['store_id'] || '',
                            email: data.email || email,
                            phone: data.phone || phone,
                            all_store_details: all_store_details
                        };

                        this.hyperpureLoginData = {
                            token: data.token,
                            store_uuid: data.store_uuid || data.storeUuid || storeUuid,
                            store_id: data.store_id || data.storeId || params['store_id'] || '',
                            entity_id: entityId,
                            email: data.email || email,
                            phone: data.phone || phone,
                            session_id: data.session_id || data.sessionId || '',
                            client_session_id: data.client_session_id || data.clientSessionId || '',
                            ActionData: {},
                            checksum: '',
                            all_store_obj: allStoreObj
                        };

                        console.log('🔑 [HYPERPURE] Fresh clientSessionId:', this.hyperpureLoginData.client_session_id);
                        console.log('🔑 [HYPERPURE] Fresh token:', this.hyperpureLoginData.token ? 'present' : 'missing');

                        // Immediately initialize iframe with fresh credentials
                        this.initializeIframe();
                    },
                    (error: any) => {
                        console.error('❌ [HYPERPURE] Fresh sign-in failed:', error);
                        console.error('❌ [HYPERPURE] Error type:', typeof error);
                        console.error('❌ [HYPERPURE] Error details:', JSON.stringify(error));

                        // Fallback: use query params directly (token may be stale)
                        console.warn('⚠️ Falling back to query param credentials');
                        let token = params['token'] || '';
                        if (token && !token.startsWith('Bearer ') && !token.startsWith('bearer ')) {
                            token = 'Bearer ' + token;
                        }

                        const fallbackStoreDetails = [{
                            store_id: params['store_id'] || '',
                            restaurant_name: params['storeName'] || params['store_name'] || '',
                            restaurant_gstno: params['storeGst'] || params['store_gst'] || '',
                            store_address: params['storeAddress'] || params['store_address'] || '',
                            store_pincode: params['storePincode'] || params['store_pincode'] || ''
                        }];

                        const fallbackAllStoreObj = {
                            store_id: params['store_id'] || '',
                            email: email,
                            phone: phone,
                            all_store_details: JSON.stringify(fallbackStoreDetails)
                        };

                        this.hyperpureLoginData = {
                            token: token,
                            store_uuid: storeUuid,
                            store_id: params['store_id'] || '',
                            entity_id: entityId,
                            email: email,
                            phone: phone,
                            session_id: params['session_id'] || '',
                            client_session_id: params['client_session_id'] || '',
                            ActionData: {},
                            checksum: '',
                            all_store_obj: fallbackAllStoreObj
                        };

                        if (token) {
                            this.initializeIframe();
                        } else {
                            this.showError('Failed to get Hyperpure credentials. No token available.');
                        }
                    }
                );
            return;
        }

        // If no query params, try other methods
        console.warn('⚠️ Missing Hyperpure credentials');
        this.tryLoadFromStorage();
    }

    /**
     * Try loading data from session storage or localStorage
     * (Only if user is already authenticated)
     */
    private tryLoadFromStorage(): void {
        if (!this.isUserAuthenticated) {
            this.showError('User not authenticated');
            return;
        }

        // Try to get store_uuid from storage, then call sign-in API
        let storedData: any = null;

        // Method 2: From session storage
        try {
            const stored = sessionStorage.getItem('hyperpureLoginData');
            if (stored) {
                storedData = JSON.parse(stored);
                console.log('✅ Login data loaded from session storage');
            }
        } catch (e) {
            console.error('Failed to parse login data from session storage:', e);
        }

        // Method 3: From localStorage (fallback)
        if (!storedData) {
            try {
                const stored = localStorage.getItem('hyperpureLoginData');
                if (stored) {
                    storedData = JSON.parse(stored);
                    console.log('✅ Login data loaded from localStorage');
                }
            } catch (e) {
                console.error('Failed to parse login data from localStorage:', e);
            }
        }

        if (!storedData) {
            this.showError('No Hyperpure login data provided. Please authenticate first.');
            return;
        }

        // If we have store_uuid, call sign-in API for fresh credentials
        if (storedData.store_uuid) {
            console.log('🔄 [HYPERPURE] Calling sign-in API with stored store_uuid...');

            const requestPayload = {
                store_uuid: storedData.store_uuid,
                store_details: [],
                user_details: {}
            };

            const entityId = storedData.entity_id || storedData.entityId || '';

            this.priceDiscoveryService.getHyperpureLoginData(entityId, requestPayload, this.userToken)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (data: any) => {
                        console.log('✅ [HYPERPURE] Fresh sign-in from storage successful:', data);

                        const storeDetailsArray = [{
                            store_id: data.hyperpureIdMd5 || storedData.store_id || '',
                            restaurant_name: data.storeName || '',
                            restaurant_gstno: data.storeGst || '',
                            store_address: data.storeAddress || '',
                            store_pincode: data.storePincode || ''
                        }];
                        const all_store_details = JSON.stringify(storeDetailsArray);

                        const allStoreObj = {
                            store_id: data.store_id || data.storeId || storedData.store_id || '',
                            email: data.email || storedData.email || '',
                            phone: data.phone || storedData.phone || '',
                            all_store_details: all_store_details
                        };

                        this.hyperpureLoginData = {
                            token: data.token,
                            store_uuid: data.store_uuid || data.storeUuid || storedData.store_uuid,
                            store_id: data.store_id || data.storeId || storedData.store_id || '',
                            email: data.email || storedData.email || '',
                            phone: data.phone || storedData.phone || '',
                            session_id: data.session_id || data.sessionId || '',
                            client_session_id: data.client_session_id || data.clientSessionId || '',
                            ActionData: {},
                            checksum: '',
                            all_store_obj: allStoreObj
                        };

                        console.log('🔑 [HYPERPURE] Fresh clientSessionId:', this.hyperpureLoginData.client_session_id);
                        this.initializeIframe();
                    },
                    (error: any) => {
                        console.error('❌ [HYPERPURE] Fresh sign-in from storage failed, using stored data:', error);
                        // Fallback to stored data
                        this.hyperpureLoginData = storedData;
                        if (storedData.token) {
                            this.initializeIframe();
                        } else {
                            this.showError('Failed to refresh Hyperpure credentials.');
                        }
                    }
                );
        } else {
            // No store_uuid, use stored data as-is
            this.hyperpureLoginData = storedData;
            this.initializeIframe();
        }
    }

    /**
     * Initialize the iframe and load Hyperpure cart
     * (Only if user is authenticated and fresh credentials are available)
     */
    private initializeIframe(): void {
        if (!this.isUserAuthenticated) {
            this.showError('User authentication required');
            return;
        }

        if (!this.hyperpureLoginData || !this.hyperpureLoginData.token) {
            this.showError('Invalid login data. Token is missing.');
            return;
        }

        // Sanitize and load iframe URL
        console.log('🎬 [HYPERPURE] Loading iframe with URL:', this.iframeUrl);
        console.log('🔑 [HYPERPURE] Using clientSessionId:', this.hyperpureLoginData.client_session_id);
        this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
        this.showIframe = true;
        document.body.style.overflow = 'hidden';

        // Wait for iframe to load, then send messages
        setTimeout(() => {
            this.isLoading = false;
        }, 2000);
    }

    /**
     * Setup message listener for iframe communication
     */
    private setupMessageListener(): void {
        window.addEventListener('message', (event: MessageEvent) => {
            // Security: Verify origin
            const isHyperpureOrigin = event.origin === this.trustedOrigin;
            const isOwnOrigin = event.origin === window.location.origin;

            if (!isHyperpureOrigin && !isOwnOrigin) {
                console.warn('❌ Rejected message from untrusted origin:', event.origin);
                return;
            }

            let response = event.data;

            // Parse JSON if string
            if (typeof response === 'string') {
                try {
                    response = JSON.parse(response);
                } catch (e) {
                    return;
                }
            }

            // Validate message structure
            if (!this.isValidMessage(response)) {
                return;
            }

            const topic = response.topic || response.Topic;

            console.log('📩 Received message:', topic);

            // Handle different message types
            if (topic === 'hyperpure_frame_loaded') {
                this.handleFrameLoaded();
            }

            if (topic === 'petpooja_store_registered') {
                if (this.listenerCalled) return;
                this.listenerCalled = true;
                this.handleStoreRegistration(response);
            }

            // Handle other Hyperpure events
            if (topic === 'hyperpure_cart_updated') {
                console.log('🛒 Cart updated in iframe');
                this.sharedService.successAlert('Cart updated successfully');
            }

            if (topic === 'hyperpure_checkout_complete') {
                console.log('✅ Checkout completed');
                this.sharedService.successAlert('Order placed successfully!');
            }
        }, false);
    }

    /**
     * Validate message structure
     */
    private isValidMessage(data: any): boolean {
        return (
            typeof data === 'object' &&
            (typeof data.Topic === 'string' || typeof data.topic === 'string')
        );
    }

    /**
     * Handle frame loaded event - Send cart load message
     * Uses the already-fresh token and client_session_id from the initial sign-in.
     * No additional sign-in call needed here.
     */
    private handleFrameLoaded(): void {
        if (this.frameLoadedHandled) {
            console.log('⚠️ Duplicate hyperpure_frame_loaded received, ignoring...');
            return;
        }
        this.frameLoadedHandled = true;

        console.log('📨 Iframe loaded, sending cart load message with existing credentials...');
        console.log('🔑 [HYPERPURE] Using token:', this.hyperpureLoginData?.token ? 'present' : 'missing');
        console.log('🔑 [HYPERPURE] Using client_session_id:', this.hyperpureLoginData?.client_session_id || 'missing');
        
        this.sendCartLoadMessage();
    }

    /**
     * Send login message to iframe (Topic 1: hyperpure_login)
     */
    private sendLoginMessage(): void {
        const tryDispatch = (attempts = 0): void => {
            try {
                if (!this.iframeElement) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe element not found after 10 attempts');
                    return;
                }

                const iframe = this.iframeElement.nativeElement;
                if (!iframe || !iframe.contentWindow) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe contentWindow not accessible');
                    return;
                }

                // Build login payload
                const payload = this.buildDispatcherData();

                // Send message to iframe
                console.log('📤 [HYPERPURE] Login payload sent to iframe:', JSON.stringify(payload, null, 2));
                iframe.contentWindow.postMessage(
                    JSON.stringify(payload),
                    this.iframeUrl
                );

                console.log('✅ Login message sent to iframe:', payload.Topic);

            } catch (err) {
                console.error('❌ Failed to send login message:', err);
                if (attempts < 5) {
                    setTimeout(() => tryDispatch(attempts + 1), 1000);
                }
            }
        };

        tryDispatch();
    }

    /**
     * Build login dispatcher data (Topic 1)
     */
    private buildDispatcherData(): any {
        return {
            Topic: 'hyperpure_login',
            Data: this.hyperpureLoginData || {}
        };
    }

    /**
     * Send cart load message to iframe (Topic 2: hyperpure_frame_load_cart)
     */
    private sendCartLoadMessage(): void {
        const tryDispatch = (attempts = 0): void => {
            try {
                if (!this.iframeElement) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe element not found after 10 attempts');
                    return;
                }

                const iframe = this.iframeElement.nativeElement;
                if (!iframe || !iframe.contentWindow) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe contentWindow not accessible');
                    return;
                }

                // Build cart load payload
                const payload = {
                    Topic: 'hyperpure_frame_load_cart',
                    Data: this.hyperpureLoginData || {}
                };

                // Send message to iframe
                console.log('📤 [HYPERPURE] Cart load payload sent to iframe:', JSON.stringify(payload, null, 2));
                iframe.contentWindow.postMessage(
                    JSON.stringify(payload),
                    this.iframeUrl
                );

                console.log('✅ Cart load message sent to iframe:', payload.Topic);

            } catch (err) {
                console.error('❌ Failed to send cart load message:', err);
                if (attempts < 5) {
                    setTimeout(() => tryDispatch(attempts + 1), 1000);
                }
            }
        };

        tryDispatch();
    }

    /**
     * Handle store registration event
     */
    private handleStoreRegistration(response: any): void {
        console.log('📝 Store registration received:', response);

        this.priceDiscoveryService.saveHyperpureStoreRegistration(response)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result: any) => {
                    this.sharedService.successAlert('Store registered successfully');
                    console.log('✅ Store registration saved:', result);
                },
                (error: any) => {
                    console.error('❌ Failed to save store registration:', error);
                    this.sharedService.errorAlert('Failed to save store registration');
                }
            );
    }

    /**
     * Reload the iframe
     */
    reloadIframe(): void {
        if (!this.isUserAuthenticated) {
            this.showError('User authentication required');
            return;
        }

        if (!this.hyperpureLoginData || !this.hyperpureLoginData.store_uuid) {
            this.showError('Hyperpure credentials missing');
            return;
        }

        // Reset the frame loaded guard so the reloaded iframe can send its cart load message
        this.frameLoadedHandled = false;

        this.sharedService.successAlert('Refreshing Hyperpure cart...');

        const requestPayload = {
            store_uuid: this.hyperpureLoginData.store_uuid,
            store_details: [],
            user_details: {}
        };

        const entityId = this.hyperpureLoginData.entity_id || this.hyperpureLoginData.entityId || '';

        this.priceDiscoveryService.getHyperpureLoginData(entityId, requestPayload, this.userToken)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (data: any) => {
                    const storeDetailsArray = [{
                        store_id: data.hyperpureIdMd5 || this.hyperpureLoginData.store_id || '',
                        restaurant_name: data.storeName || '',
                        restaurant_gstno: data.storeGst || '',
                        store_address: data.storeAddress || '',
                        store_pincode: data.storePincode || ''
                    }];
                    const all_store_details = JSON.stringify(storeDetailsArray);

                    const allStoreObj = {
                        store_id: data.store_id || data.storeId || this.hyperpureLoginData.store_id || '',
                        email: data.email || this.hyperpureLoginData.email || '',
                        phone: data.phone || this.hyperpureLoginData.phone || '',
                        all_store_details: all_store_details
                    };

                    this.hyperpureLoginData = {
                        ...this.hyperpureLoginData,
                        token: data.token,
                        store_uuid: data.store_uuid || data.storeUuid || this.hyperpureLoginData.store_uuid,
                        store_id: data.store_id || data.storeId || this.hyperpureLoginData.store_id || '',
                        email: data.email || this.hyperpureLoginData.email || '',
                        phone: data.phone || this.hyperpureLoginData.phone || '',
                        session_id: data.session_id || data.sessionId || '',
                        client_session_id: data.client_session_id || data.clientSessionId || '',
                        ActionData: {},
                        checksum: '',
                        all_store_obj: allStoreObj
                    };

                    if (this.iframeUrl) {
                        this.iframeUrlSafe = null;
                        this.isLoading = true;

                        setTimeout(() => {
                            this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
                            setTimeout(() => this.isLoading = false, 2000);
                        }, 10);
                    }
                },
                (error: any) => {
                    console.error('❌ Failed to refresh credentials:', error);
                    this.sharedService.errorAlert('Failed to refresh Hyperpure credentials. Please try again.');
                }
            );
    }

    /**
     * Close the iframe and go back
     */
    closeIframe(): void {
        this.showIframe = false;
        document.body.style.overflow = '';

        // Navigate back to purchase manager
        this.router.navigate(['/purchase-manager']);
    }

    /**
     * Show error message and redirect
     */
    private showError(message: string): void {
        this.sharedService.errorAlert(message);
        console.error('❌', message);

        // Redirect after 3 seconds
        setTimeout(() => {
            // Redirect to login if authentication failed, otherwise to purchase-manager
            if (!this.isUserAuthenticated) {
                this.router.navigate(['/login']);
            } else {
                this.router.navigate(['/purchase-manager']);
            }
        }, 3000);
    }

    /**
     * Manual trigger to send login message (for debugging)
     */
    manualSendLogin(): void {
        console.log('🔧 Manual login trigger');
        this.sendLoginMessage();
    }

    /**
     * Manual trigger to send cart load message (for debugging)
     */
    manualSendCartLoad(): void {
        console.log('🔧 Manual cart load trigger');
        this.sendCartLoadMessage();
    }
}
